import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Archive, Plus } from 'lucide-react';
import { Button } from '../../components/ui/button.tsx';
import { ConfirmDialog } from '../../components/ui/confirm-dialog.tsx';
import { useFunnel, useLinks, useProduct, useTheory, useUpdateTheory } from '../../lib/hooks.ts';
import { CreateTrackingLinkDialog } from './CreateTrackingLinkDialog.tsx';
import { DailyFunnelChart } from './DailyFunnelChart.tsx';
import { FunnelView } from './FunnelView.tsx';
import { LinksList } from './LinksList.tsx';

export function TheoryPage(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [createOpen, setCreateOpen] = useState(false);
  const [confirmArchive, setConfirmArchive] = useState(false);

  const theory = useTheory(id);
  const product = useProduct(theory.data?.theory.productId);
  const funnel = useFunnel(id);
  const links = useLinks(id);
  const updateMutation = useUpdateTheory();

  if (theory.isLoading) {
    return <div className="text-fg-muted">Загрузка…</div>;
  }
  if (!theory.data) {
    return (
      <div className="card">
        <p className="mb-3 text-fg-muted">Теория не найдена.</p>
        <Link to="/" className="text-accent">
          ← К обзору
        </Link>
      </div>
    );
  }

  const t = theory.data.theory;
  const isArchived = t.status === 'archived';

  async function handleArchive(): Promise<void> {
    setConfirmArchive(false);
    await updateMutation.mutateAsync({ id: t.id, patch: { status: 'archived' } });
    navigate(`/products/${t.productId}`, { replace: true });
  }

  return (
    <div>
      <Link
        to={product.data ? `/products/${t.productId}` : '/'}
        className="mb-4 inline-flex items-center gap-1 text-sm text-fg-muted hover:text-fg"
      >
        <ArrowLeft className="h-3.5 w-3.5" />
        {product.data?.product.name ?? 'Назад'}
      </Link>

      <div className="mb-6 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-semibold tracking-tight">
            {t.name}
            {isArchived ? (
              <span className="rounded bg-bg-elevated px-2 py-0.5 text-xs font-normal text-fg-muted">
                В архиве
              </span>
            ) : null}
          </h1>
          <div className="mt-1 font-mono text-sm text-fg-muted">{t.slug}</div>
        </div>
        {!isArchived ? (
          <div className="flex gap-2">
            <Button variant="ghost" onClick={() => setConfirmArchive(true)}>
              <Archive className="h-4 w-4" />
              Архивировать
            </Button>
          </div>
        ) : null}
      </div>

      <section className="mb-8">
        <h2 className="mb-3 text-sm font-medium uppercase tracking-wider text-fg-muted">Воронка</h2>
        <FunnelView
          data={funnel.data?.steps}
          loading={funnel.isLoading}
          impressionsTotal={funnel.data?.impressions_total ?? 0}
        />
      </section>

      <section className="mb-8">
        <h2 className="mb-3 text-sm font-medium uppercase tracking-wider text-fg-muted">
          Воронка по дням
        </h2>
        <DailyFunnelChart theoryId={t.id} />
      </section>

      <section>
        <div className="mb-3 flex items-center justify-between">
          <h2 className="text-sm font-medium uppercase tracking-wider text-fg-muted">
            Tracking-ссылки
          </h2>
          {!isArchived ? (
            <Button variant="primary" size="sm" onClick={() => setCreateOpen(true)}>
              <Plus className="h-4 w-4" />
              Новая ссылка
            </Button>
          ) : null}
        </div>
        <LinksList
          items={links.data?.items}
          loading={links.isLoading}
          isArchived={isArchived}
          onCreate={() => setCreateOpen(true)}
        />
      </section>

      <CreateTrackingLinkDialog open={createOpen} onOpenChange={setCreateOpen} theoryId={t.id} />

      <ConfirmDialog
        open={confirmArchive}
        title="Архивировать теорию?"
        description="После архивации tracking-ссылки этой теории перестанут принимать события. Исторические данные останутся, но новые конверсии не записываются."
        confirmLabel="Архивировать"
        confirmVariant="danger"
        onConfirm={() => void handleArchive()}
        onCancel={() => setConfirmArchive(false)}
      />
    </div>
  );
}
